import { Action, actionCreator } from 'app/gamma/src/util/actions';
import { createReducer } from 'app/gamma/src/util/reducers';
import {
  ADD_NEW_EMAIL_ERROR,
  ADD_NEW_EMAIL_REQUEST,
  ADD_NEW_EMAIL_SUCCESS,
  AddNewEmailErrorAction,
  SAVE_PROFILE_START,
  SAVE_PROFILE_COMPLETE,
  SAVE_PROFILE_RESET,
  SAVE_PROFILE_ERROR,
  SaveProfileErrorAction,
  SaveMessages,
} from 'app/gamma/src/modules/state/models/members';

const RESET_ADD_EMAIL_UI = Symbol('ui/RESET_ADD_EMAIL_UI');
type ResetAddEmailUiAction = Action<typeof RESET_ADD_EMAIL_UI, null>;
export const resetAddEmailUi = actionCreator<ResetAddEmailUiAction>(
  RESET_ADD_EMAIL_UI,
);

export enum AddEmailErrors {
  None,
  TheEmailAddressIsAlreadyInUse,
  ThisEmailIsAlreadyOnThisAccount,
  InvalidEmailAddress,
  TheEmailAddressIsFromAForbiddenDomain,
  EmailAddressIsNotOnAnApprovedDomain,
  Unknown,
}

const addEmailErrorMapping: { [key: string]: AddEmailErrors } = {
  'the email address is already in use':
    AddEmailErrors.TheEmailAddressIsAlreadyInUse,
  'this email is already on this account':
    AddEmailErrors.ThisEmailIsAlreadyOnThisAccount,
  'invalid email address': AddEmailErrors.InvalidEmailAddress,
  'the email address is from a forbidden domain':
    AddEmailErrors.TheEmailAddressIsFromAForbiddenDomain,
  'email address is not on an approved domain':
    AddEmailErrors.EmailAddressIsNotOnAnApprovedDomain,
};

export enum SaveProfileErrors {
  None,
  BioTooLong,
  FullNameTooLong,
  FullNameTooShort,
  FullNameHasUrl,
  InitialsWrongLength,
  UsernameInvalid,
  UsernameTaken,
  UsernameTooLong,
  UsernameTooShort,
  Unknown,
}

const saveProfileErrorMapping: { [key: string]: SaveProfileErrors } = {
  'username is taken': SaveProfileErrors.UsernameTaken,
  'username must be at least 3 characters': SaveProfileErrors.UsernameTooShort,
  'username must be at most 100 characters': SaveProfileErrors.UsernameTooLong,
  'username is invalid: only lowercase letters, underscores, and numbers are allowed':
    SaveProfileErrors.UsernameInvalid,
  'full name must be at least 1 character': SaveProfileErrors.FullNameTooShort,
  'full name must be at most 100 characters': SaveProfileErrors.FullNameTooLong,
  'full name must not contain a url': SaveProfileErrors.FullNameHasUrl,
  'initials must contain 1-4 characters': SaveProfileErrors.InitialsWrongLength,
  'invalid value for bio': SaveProfileErrors.BioTooLong,
};

export const PROFILE_ALERT_AREA = 'PROFILE_ALERT_AREA';
interface MemberHomeProfileSavingState {
  saveMessage: SaveMessages;
  saveErrors?: SaveProfileErrors[];
}

const initialSavingState: MemberHomeProfileSavingState = {
  saveMessage: SaveMessages.SAVE,
};

interface MemberHomeAddEmailState {
  addingEmailRequestActive: boolean;
  addingEmailSuccess: boolean;
  addingEmailError: AddEmailErrors;
}

const initialAddEmailState: MemberHomeAddEmailState = {
  addingEmailRequestActive: false,
  addingEmailSuccess: false,
  addingEmailError: AddEmailErrors.None,
};

export interface MemberHomeProfileState
  extends MemberHomeProfileSavingState,
    MemberHomeAddEmailState {}

export const initialMemberHomeProfileState: MemberHomeProfileState = {
  ...initialSavingState,
  ...initialAddEmailState,
};

export const MemberHomeProfileReducer = createReducer(
  initialMemberHomeProfileState,
  {
    [SAVE_PROFILE_START](state) {
      return {
        ...state,
        saveMessage: SaveMessages.SAVING,
      };
    },

    [SAVE_PROFILE_COMPLETE](state) {
      return {
        ...state,
        saveMessage: SaveMessages.SAVED,
        ...('saveErrors' in state ? { saveErrors: undefined } : {}),
      };
    },

    [SAVE_PROFILE_RESET](state) {
      return { ...state, ...initialSavingState };
    },

    [SAVE_PROFILE_ERROR](state, { payload }: SaveProfileErrorAction) {
      const error =
        typeof payload === 'string'
          ? [saveProfileErrorMapping[payload] || SaveProfileErrors.Unknown]
          : payload;
      return {
        ...state,
        saveErrors: error,
      };
    },

    [ADD_NEW_EMAIL_REQUEST](state) {
      return {
        ...state,
        addingEmailRequestActive: true,
        addingEmailSuccess: false,
        addingEmailError: AddEmailErrors.None,
      };
    },

    [ADD_NEW_EMAIL_SUCCESS](state) {
      return {
        ...state,
        addingEmailRequestActive: false,
        addingEmailSuccess: true,
        addingEmailError: AddEmailErrors.None,
      };
    },

    [ADD_NEW_EMAIL_ERROR](state, { payload }: AddNewEmailErrorAction) {
      return {
        ...state,
        addingEmailRequestActive: false,
        addingEmailSuccess: false,
        addingEmailError:
          addEmailErrorMapping[payload] || AddEmailErrors.Unknown,
      };
    },

    [RESET_ADD_EMAIL_UI](state) {
      return { ...state, ...initialAddEmailState };
    },
  },
);
